import { useState } from 'react';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';

interface IncludeAllDropdownProps {
  allRowsChecked: boolean;
  anyRowsChecked: boolean;
  allRowsCheckedInView: boolean;
  anyRowsCheckedInView: boolean;
  handleSelectAll: (params: { inViewOnly: boolean }) => Promise<void>;
  handleClearAll: (params: { inViewOnly: boolean }) => Promise<void>;
}

export const IncludeAllDropdown = ({
  allRowsChecked,
  allRowsCheckedInView,
  anyRowsCheckedInView,
  anyRowsChecked,
  handleSelectAll,
  handleClearAll,
}: IncludeAllDropdownProps) => {
  const [loading, setLoading] = useState(false);

  const selectAllHandler = async ({ inViewOnly }: { inViewOnly: boolean }) => {
    try {
      if (allRowsChecked) return;
      setLoading(true);
      await handleSelectAll({ inViewOnly });
    } finally {
      setLoading(false);
    }
  };

  const clearAllHandler = async ({ inViewOnly }: { inViewOnly: boolean }) => {
    try {
      if (inViewOnly ? !anyRowsCheckedInView : !anyRowsChecked) return;
      setLoading(true);
      await handleClearAll({ inViewOnly });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      {loading ? (
        <Icon className="lucide--loader-circle h-5 w-5 animate-spin text-teal-500" />
      ) : (
        <Checkbox
          className="h-5 w-5 rounded-full"
          checked={allRowsChecked}
          onCheckedChange={() => selectAllHandler({ inViewOnly: false })}
        />
      )}
      <DropdownMenuTrigger asChild>
        <Button variant="ghost">
          <Icon className="lucide--chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => selectAllHandler({ inViewOnly: false })} disabled={allRowsChecked}>
          Select All
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => selectAllHandler({ inViewOnly: true })} disabled={allRowsCheckedInView}>
          Select All On Page
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => clearAllHandler({ inViewOnly: false })} disabled={!anyRowsChecked}>
          Clear All
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => clearAllHandler({ inViewOnly: true })} disabled={!anyRowsCheckedInView}>
          Clear All On Page
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
